// Sign-up (F-IAM-02, F-CMP-06, spec/07 §2.1, §3.1): a public route, `/sign-up`, on the existing
// better-auth backend. `POST /v1/auth/sign-up/email` creates the account with the terms and privacy
// versions shown (`GET /v1/meta`'s `brand.legal`) and sends a code to the address;
// `POST /v1/auth/email-otp/verify-email` spends it. Verifying an email never signs a person in on its own
// (its `token` is always `null`, unlike phone verification) — so, once it succeeds, this screen signs in
// with the password just chosen, exactly as `PasswordForm` does, to land in the app. The code step stays:
// an address is proved before an account holds it.
//
// **From an invitation** (ADR-0046): when an invitation waits in this browser (`invitation-token.ts`), the
// screen reads it (`POST /v1/invitations/preview`) and, for an email invitation, prefills the invited
// address and locks it — accepting needs exactly that address, verified — then goes back to accept it.
//
// **Continue with Google** (F-IAM-03), where the deployment has it switched on: the same consent is asked
// first, and the account is created with it on Google's way back. Google's verified address is the
// account's, verified. The server refuses a competing vendor's domain either way (`SIGN_UP_REFUSED`).
//
// `?redirect=` takes a visitor back into an in-progress flow — the same rule `/sign-in` validates by (a
// path, never `//host`); `?error=` is what a refused Google round trip came back with.

import type { SocialProvider } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Stepper, TextField } from '@clavian/ui-web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { fetchBrandLegal } from '../api/meta';
import { queryKeys } from '../api/queries';
import { AuthLayout } from './AuthLayout';
import { useAuthFailure } from './auth-errors';
import { ConsentLabel } from './consent';
import { Failure, TextInput } from './form-controls';
import { afterSignIn, pendingInvitationToken } from './invitation-token';
import { pendingInvitationOf, useInvitationPreview, type PendingInvitation } from './invitation-preview';
import { SecondFactorForm } from './SecondFactorForm';
import { ContinueWithProviders, CreateAccountDialog, isSocialProvider, socialErrorKey } from './social';

type Step = { readonly kind: 'form' } | { readonly kind: 'verify'; readonly email: string; readonly password: string } | { readonly kind: 'second-factor' };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The invitation waiting in this browser, read for its invited address — only on the way to accept it. */
function useWaitingInvitation(enabled: boolean): PendingInvitation | undefined {
  const query = useInvitationPreview(enabled ? pendingInvitationToken() : undefined);
  return pendingInvitationOf(query.data);
}

export function SignUpPage() {
  const { t } = useTranslation('console');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/sign-up' });
  const [step, setStep] = useState<Step>({ kind: 'form' });
  // Only on the way to accept it: someone signing up for anything else is not held to an invitation's address.
  const invitation = useWaitingInvitation(search.redirect === '/invitations/accept');
  const invitedEmail = invitation?.channel === 'email' ? invitation.contact.email : undefined;

  const signedIn = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await queryClient.refetchQueries({ queryKey: queryKeys.session });
    await navigate({ to: afterSignIn(search.redirect) });
  };

  let body: ReactNode;
  if (step.kind === 'second-factor') {
    body = <SecondFactorForm intro={t('secondFactor.intro')} onVerified={signedIn} />;
  } else if (step.kind === 'verify') {
    body = (
      <VerifyEmailForm
        email={step.email}
        password={step.password}
        onSignedIn={signedIn}
        onSecondFactor={() => setStep({ kind: 'second-factor' })}
        onChangeEmail={() => setStep({ kind: 'form' })}
        locked={invitedEmail !== undefined}
      />
    );
  } else {
    body = (
      <SignUpForm
        lockedEmail={invitedEmail}
        redirect={search.redirect}
        error={search.error}
        provider={search.provider}
        onSignedUp={(email, password) => setStep({ kind: 'verify', email, password })}
      />
    );
  }

  return (
    <AuthLayout
      title={step.kind === 'verify' ? t('signUp.verify.title') : t('signUp.title')}
      subtitle={step.kind === 'form' ? (invitedEmail === undefined ? t('signUp.intro') : t('signUp.invitedIntro', { organization: invitation?.organizationName ?? '' })) : undefined}
      footer={t('signUp.footer')}
      progress={
        <Stepper
          current={step.kind === 'form' ? 0 : 1}
          steps={[
            { id: 'details', label: t('signUp.steps.details') },
            { id: 'verify', label: t('signUp.steps.verify') },
          ]}
        />
      }
    >
      {body}
    </AuthLayout>
  );
}

interface SignUpFormValues {
  name: string;
  email: string;
  password: string;
  consent: boolean;
  marketing: boolean;
}

function SignUpForm({
  lockedEmail,
  redirect,
  error,
  provider,
  onSignedUp,
}: {
  /** The invited address: prefilled, and not to be changed — the invitation is for it alone. */
  readonly lockedEmail: string | undefined;
  readonly redirect: string | undefined;
  /** What a provider round trip came back with, refused, and which provider this console named. */
  readonly error: string | undefined;
  readonly provider?: string | undefined;
  readonly onSignedUp: (email: string, password: string) => void;
}) {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure('signUp');
  const api = useApi();
  const fromProvider = socialErrorKey(error);
  const returned = isSocialProvider(provider) ? provider : undefined;
  // A provider account with no Clavian account yet: "Create your Clavian account", the terms, then the provider again.
  const [creating, setCreating] = useState<SocialProvider | null>(fromProvider === 'social.error.noAccount' && returned !== undefined ? returned : null);
  const [failure, setFailure] = useState<string | null>(
    fromProvider === undefined || creating !== null ? null : t(fromProvider, { provider: t(`social.provider.${returned ?? 'google'}`) }),
  );
  const legal = useQuery({ queryKey: queryKeys.meta, queryFn: () => fetchBrandLegal(api), staleTime: 300_000 });
  // The invited address, when there is one, is not a field of the form: it is shown locked and sent as it is.
  const form = useForm<SignUpFormValues>({ defaultValues: { name: '', email: '', password: '', consent: false, marketing: false } });

  const consentOf = () => {
    if (legal.data === undefined) return undefined;
    const marketing = form.getValues('marketing');
    return { terms: legal.data.terms.version, privacy: legal.data.privacy.version, ...(marketing ? { marketing: true } : {}) };
  };

  const submit = form.handleSubmit(async ({ name, email: typed, password }) => {
    setFailure(null);
    const consent = consentOf();
    if (consent === undefined) return;
    const address = (lockedEmail ?? typed).trim();
    try {
      await api.signUpWithEmailAndPassword({ body: { name, email: address, password, consent } });
      onSignedUp(address, password);
    } catch (caught) {
      setFailure(failureText(caught));
    }
  });

  const here = redirect === undefined ? '/sign-up' : `/sign-up?redirect=${encodeURIComponent(redirect)}`;

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput control={form.control} name="name" label={t('signUp.name')} autoComplete="name" rules={{ required: t('signIn.required') }} />
      {lockedEmail === undefined ? (
        <TextInput
          control={form.control}
          name="email"
          label={t('signUp.email')}
          type="email"
          inputMode="email"
          autoComplete="email"
          rules={{
            required: t('signIn.required'),
            validate: (value: string) => EMAIL.test(value.trim()) || t('signUp.failure.invalidEmail'),
          }}
        />
      ) : (
        <LockedEmail email={lockedEmail} />
      )}
      <TextInput
        control={form.control}
        name="password"
        label={t('signUp.password')}
        description={t('signUp.passwordHint')}
        type="password"
        autoComplete="new-password"
        rules={{
          required: t('signIn.required'),
          validate: (value: string) => value.length >= 8 || t('signUp.failure.passwordTooShort'),
        }}
      />
      {legal.isPending ? (
        <p className="console-muted" role="status">
          {t('app.loading')}
        </p>
      ) : legal.data === undefined ? (
        <p className="console-alert" role="status">
          {t('signUp.legal.unavailable')}
        </p>
      ) : null}
      <Controller
        control={form.control}
        name="consent"
        rules={{ validate: (value: boolean) => value || t('signUp.consent.required') }}
        render={({ field, fieldState }) => (
          <>
            {/* The two documents are linked once, inside the sentence that agrees to them — not a "Read the:" line of links above a
                checkbox that names them again. */}
            <Checkbox
              label={<ConsentLabel legal={legal.data} />}
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              aria-invalid={fieldState.error !== undefined}
              disabled={legal.data === undefined}
            />
            <Failure message={fieldState.error?.message ?? null} />
          </>
        )}
      />
      <Controller
        control={form.control}
        name="marketing"
        render={({ field }) => (
          <Checkbox
            label={t('signUp.consent.marketing')}
            checked={field.value}
            onCheckedChange={(checked) => field.onChange(checked === true)}
            disabled={legal.data === undefined}
          />
        )}
      />
      <Button type="submit" variant="primary" busy={form.formState.isSubmitting} disabled={legal.data === undefined}>
        {t('signUp.submit')}
      </Button>
      {/* Where a password sign-up lands too: back into the flow that sent the visitor here, or to an invitation waiting
          in this browser, or home. No terms first: a new account is asked for them in the dialog (owner, 2026-10-08). */}
      <ContinueWithProviders landing={afterSignIn(redirect)} onError={here} />
      <CreateAccountDialog provider={creating} landing={afterSignIn(redirect)} onError={here} onClose={() => setCreating(null)} />
      <p className="auth__alt">
        <Link to="/sign-in" search={redirect === undefined ? {} : { redirect }}>
          {t('signUp.signInInstead')}
        </Link>
      </p>
    </form>
  );
}

/** The invited address, shown as the account's and not to be changed: the invitation is for it alone. */
function LockedEmail({ email }: { readonly email: string }) {
  const { t } = useTranslation('console');
  return <TextField label={t('signUp.email')} description={t('signUp.invitedEmailHint')} type="email" name="email" value={email} readOnly />;
}

function VerifyEmailForm({
  email,
  password,
  onSignedIn,
  onSecondFactor,
  onChangeEmail,
  locked,
}: {
  readonly email: string;
  readonly password: string;
  readonly onSignedIn: () => Promise<void>;
  readonly onSecondFactor: () => void;
  readonly onChangeEmail: () => void;
  /** The address is the invitation's: there is no other to change it to. */
  readonly locked: boolean;
}) {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure('signUp');
  const api = useApi();
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submit = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    setNotice(null);
    try {
      await api.verifyEmailOTP({ body: { email, otp: code.trim() } });
      const result = await api.signInEmail({ body: { email, password } });
      if (result.twoFactorRedirect === true) onSecondFactor();
      else await onSignedIn();
    } catch (error) {
      setFailure(failureText(error));
    }
  });

  const resend = async () => {
    setFailure(null);
    setNotice(null);
    try {
      await api.sendEmailVerificationOTP({ body: { email, type: 'email-verification' } });
      setNotice(t('signUp.verify.resent', { email }));
    } catch (error) {
      setFailure(failureText(error));
    }
  };

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate>
      <p>{t('signUp.verify.intro', { email })}</p>
      {notice ? (
        <p role="status" className="console-muted">
          {notice}
        </p>
      ) : null}
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="code"
        label={t('signUp.verify.code')}
        description={t('signUp.verify.codeHint', { email })}
        inputMode="numeric"
        autoComplete="one-time-code"
        numeric
        rules={{ required: t('signIn.required') }}
      />
      <div className="console-form__actions">
        <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
          {t('signUp.verify.verify')}
        </Button>
        <Button variant="ghost" onClick={() => void resend()}>
          {t('signUp.verify.resend')}
        </Button>
        {locked ? null : (
          <Button variant="ghost" onClick={onChangeEmail}>
            {t('signUp.verify.changeEmail')}
          </Button>
        )}
      </div>
    </form>
  );
}
